<template>
  <div class="border border-gray-200 rounded-lg p-4 py-2 mb-4">
    <div class="flex flex-wrap gap-x-4 gap-y-3 items-center">
      <div
        class="basis-[calc(50%-8px)] sm:basis-[140px] shrink-0 min-w-0 flex flex-col text-sm text-start"
        :title="`#${exceptionId} - ${formattedDate}`"
      >
        <span class="text-[#52526F] text-xs font-bold truncate leading-[14px] tracking-[0.06px]"
          >#{{ exceptionId }}</span
        >
        <span class="text-textPrimary text-md mb-1 font-bold truncate">Data do plantão</span>
        <span class="truncate">{{ formattedDate }}</span>
      </div>

      <div class="hidden sm:block self-stretch w-px bg-gray-200" />

      <div
        class="basis-[calc(50%-8px)] sm:basis-[130px] grow shrink-0 min-w-0 flex flex-col text-sm text-start"
        :title="`${status} - Prejuízo previsto: ${formattedPrejudice}`"
      >
        <span class="text-textPrimary font-bold text-md mb-1 truncate">Status</span>
        <StatusBadge :status="status" />
        <span
          v-if="hasPendingReversal"
          class="mt-1 inline-flex w-fit items-center rounded-full border border-warning bg-warning/20 px-2 py-0.5 text-xs font-bold text-yellow-800"
          data-testid="exception-card-reversal-pending-badge"
        >
          Reversão pendente
        </span>
        <span class="text-textPrimary text-xs mt-1 truncate leading-[14px] tracking-[0.06px]"
          >Prejuízo previsto:
          <strong class="text-negative font-bold">{{ formattedPrejudice }}</strong></span
        >
      </div>

      <div
        class="basis-[calc(50%-8px)] sm:basis-[130px] grow shrink-0 min-w-0 flex flex-col text-sm text-start"
        :title="doctorName + (doctorCrm ? ` - ${doctorCrm}` : '')"
      >
        <span class="text-textPrimary font-bold text-md mb-1 truncate">Médico</span>
        <span class="truncate">{{ doctorName }}</span>
        <small
          v-if="doctorCrm"
          class="text-textPrimary text-sm font-bold truncate block leading-[18px] tracking-[0.07px]"
          >{{ doctorCrm }}</small
        >
      </div>

      <div
        class="basis-[calc(50%-8px)] sm:basis-[130px] grow shrink-0 min-w-0 flex flex-col text-sm text-start"
        :title="`${scale || 'Não informada'} - ${unit || 'Não informada'}`"
      >
        <span class="text-textPrimary font-bold text-md mb-1 truncate">Escala</span>
        <span class="truncate">
          {{ scale || 'Não informada' }}
        </span>
        <small class="text-textPrimary truncate block">
          {{ unit || 'Não informada' }}
        </small>
      </div>

      <div
        v-if="flags.enabledApprovalEngine"
        class="basis-[calc(50%-8px)] sm:basis-[90px] grow shrink-0 min-w-0 overflow-hidden flex flex-col text-sm text-start"
        title="Progresso"
      >
        <span class="text-textPrimary font-bold text-md mb-1 truncate">Progresso</span>
        <ApprovalProgressBar v-if="progressInstance" :approval-instance="progressInstance" />
        <span v-else class="text-neutralLight text-xs">-</span>
      </div>

      <div class="basis-full sm:basis-auto sm:ml-auto shrink-0 flex justify-end">
        <OutlinedButton
          text="Ver detalhes"
          width="130px"
          height="32px"
          data-testid="exception-card-details-button"
          @click="$emit('details', props.exceptionId)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import type {
  ApprovalInstanceDto,
  ExceptionPendingAction
} from '@/Network/Types/Requests/DoctorStationExceptionRequests'
import {
  Doctor,
  ExceptionStatusEnum
} from '@/Network/Types/Requests/DoctorStationExceptionRequests'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { getFirstAndSecondName } from '@/utils/getFirstAndSecondName'
import StatusBadge from '@/components/designSystem/Badges/StatusBadge.vue'
import ApprovalProgressBar from './ApprovalProgressBar.vue'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { isReversalPending } from '@/utils/exception-reversal'

const { flags } = useFeatureFlags()

const props = defineProps({
  date: {
    type: String,
    default: ''
  },
  status: {
    type: String as () => ExceptionStatusEnum,
    required: true,
    validator: (val: ExceptionStatusEnum) => Object.values(ExceptionStatusEnum).includes(val)
  },
  doctor: {
    type: Object as () => Doctor | null,
    default: null
  },
  scale: {
    type: String,
    default: ''
  },
  unit: {
    type: String,
    default: ''
  },
  prejudice: {
    type: String,
    required: true
  },
  exceptionId: {
    type: [Number],
    required: true
  },
  approvalInstance: {
    type: Object as () => ApprovalInstanceDto | null,
    default: null
  },
  reversalInstance: {
    type: Object as () => ApprovalInstanceDto | null,
    default: null
  },
  pendingAction: {
    type: String as () => ExceptionPendingAction | null,
    default: null
  }
})

const hasPendingReversal = computed(() =>
  isReversalPending({ pendingAction: props.pendingAction }, flags.enabledApprovalEngine)
)

// Com reversão pendente o progresso que importa é o da reversão.
const progressInstance = computed<ApprovalInstanceDto | null>(() =>
  hasPendingReversal.value && props.reversalInstance
    ? props.reversalInstance
    : props.approvalInstance
)

defineEmits<{
  (e: 'details', exceptionId: number): void
}>()

const formattedDate = computed(() => {
  if (props.date) {
    try {
      return format(new Date(props.date), 'dd/MM/yyyy', { locale: ptBR })
    } catch (e) {
      console.error('Error formatting date:', e)
      return props.date
    }
  }
  return 'Data não informada'
})

const formattedPrejudice = computed(() => {
  if (props.prejudice) {
    const numericValue = parseFloat(props.prejudice)
    if (!isNaN(numericValue)) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(numericValue)
    }
  }
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(0)
})

const doctorName = computed(() => {
  if (props.doctor && props.doctor.name) {
    return getFirstAndSecondName(props.doctor.name)
  }
  return 'Médico não informado'
})

const doctorCrm = computed(() => {
  if (props.doctor && props.doctor.crm && props.doctor.crm.number && props.doctor.crm.uf) {
    return `${props.doctor.crm.number}-${props.doctor.crm.uf}`
  }
  return ''
})
</script>
